/**
 * Cookie consent state — gates the GA4 + AdSense initialisation.
 *
 * EU users (GDPR + ePrivacy Directive) must opt in to non-essential
 * cookies before tracking scripts load. We don't run any tracking
 * or ad code until the user picks `accepted`; if they pick `rejected`
 * (or close the tab without choosing) nothing loads at all.
 *
 * Receipt content is unaffected by this — it never leaves the
 * browser regardless of consent state. The banner is purely about
 * GA4 / AdSense.
 *
 * Storage: localStorage key `rr-consent`, value `accepted` | `rejected`.
 * Wrapped in try/catch so locked-down storage (Safari Private mode,
 * cookies-blocked) doesn't break the app — a failed read just looks
 * like a fresh visit, the banner shows again next time, and trackers
 * stay disabled in the meantime (privacy-preserving default).
 */

export type ConsentValue = 'accepted' | 'rejected';

const STORAGE_KEY = 'rr-consent';

export function loadConsent(): ConsentValue | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'accepted' || v === 'rejected') return v;
    return null;
  } catch {
    return null;
  }
}

export function setConsent(v: ConsentValue): void {
  try {
    localStorage.setItem(STORAGE_KEY, v);
  } catch {
    // ignore — caller proceeds with the in-memory choice for this
    // session even if persistence is unavailable
  }
}

export function clearConsent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function hasConsent(): boolean {
  return loadConsent() === 'accepted';
}
